import { useState } from "react";

const chapters = [
  {
    id: "ch1",
    tag: "Chapter 01",
    date: "Pre-Outbreak",
    title: "The Protector",
    subtitle: "Before the world ended",
    color: "#C8922A",
    image: "https://public.readdy.ai/ai/img_res/04a5c2b8d1634d2bfee4353f7ef918ad.jpg",
    content: "Before the world ended, Jordan Reyes was a star softball pitcher on a scholarship — a competitor, not a fighter. She was the oldest of three, always looking out for her younger siblings while her single mother worked double shifts. Her strength was born from discipline and a deep-seated need to protect those she loved. It was this instinct that would later define her survival.",
  },
  {
    id: "ch2",
    tag: "Chapter 02",
    date: "Green Monday",
    title: "The Fall",
    subtitle: "The day the cities fell",
    color: "#8B1A1A",
    image: "https://readdy.ai/api/search-image?query=college%20campus%20chaos%20emergency%20green%20alert%20phones%20glowing%2C%20panicked%20crowd%2C%20dark%20dramatic%20lighting%2C%20emergency%20broadcast%20screens%2C%20broken%20windows%2C%20people%20running%2C%20dystopian%20event%2C%20cinematic%20apocalyptic%20scene%2C%20foggy%20dark%20atmosphere%2C%20green%20tinted%20emergency%20lights&width=600&height=400&seq=story002&orientation=landscape",
    content: "The outbreak didn\'t start with a bang — it was a slow, creeping horror the news called \'Gray Fever.\' On Green Monday, chaos erupted during a lecture. Jordan fought her way through the campus with Aidan, her athletic reflexes saving them more than once. Her softball bat — nicknamed \'Home-Run\' and always in her trunk — saw its first violent use that day, smashing a window to find shelter.",
  },
  {
    id: "ch3",
    tag: "Chapter 03",
    date: "Months 1–6",
    title: "The Crucible",
    subtitle: "The Supermarket Massacre",
    color: "#8B1A1A",
    image: "https://readdy.ai/api/search-image?query=dark%20fortified%20supermarket%20interior%20post-apocalyptic%2C%20barricaded%20shelves%2C%20dim%20flickering%20emergency%20lights%2C%20survivor%20camp%20inside%2C%20gritty%20shadows%2C%20scattered%20canned%20goods%2C%20broken%20glass%2C%20dusty%20dark%20atmosphere%2C%20dystopian%20cinematic%20photography%2C%20horror%20abandoned%20store&width=600&height=400&seq=story003&orientation=landscape",
    content: "They found a group holed up in a fortified supermarket. Jordan became a scavenger — her speed invaluable. Aidan became the heart of the group, turning meager rations into meals that lifted spirits. Then a massive horde breached the gates. In the panicked, bloody chaos, Jordan and Aidan were separated. The last thing she saw was his face — eyes wide with fear, screaming her name.",
  },
  {
    id: "ch4",
    tag: "Chapter 04",
    date: "Months 7–12",
    title: "The Wraith",
    subtitle: "The Winter of Wraith",
    color: "#C8922A",
    image: "https://readdy.ai/api/search-image?query=close%20up%20barbed%20wire%20wrapped%20baseball%20bat%20on%20cracked%20concrete%20floor%2C%20rust%20and%20dried%20blood%2C%20dark%20dramatic%20lighting%2C%20gritty%20macro%20photography%2C%20post-apocalyptic%20weapon%2C%20shadow%20contrast%2C%20ominous%20dark%20aesthetic%2C%20cinematic%20still&width=600&height=400&seq=story004&orientation=landscape",
    content: "The months that followed were dark. Jordan became a ghost — silent, ruthless, efficient. She wrapped her bat in barbed wire and honed her skills not for sport, but for survival. The memory of failing Aidan fueled a cold fury. She was no longer just surviving — she was punishing the world that took him. Survivors started calling her \'The Wraith\': a grim, silent girl who moved like wind and hit like a freight train.",
  },
  {
    id: "ch5",
    tag: "Chapter 05",
    date: "Year 1 — Present",
    title: "The Signal",
    subtitle: "A voice through the static",
    color: "#C8922A",
    image: "https://readdy.ai/api/search-image?query=abandoned%20ham%20radio%20shack%20interior%2C%20vintage%20radio%20equipment%20glowing%20with%20static%2C%20dim%20single%20light%20bulb%2C%20dust%20particles%2C%20dark%20wooden%20walls%2C%20analog%20dials%20and%20meters%2C%20mysterious%20atmospheric%20glow%2C%20cinematic%20post-apocalyptic%20mood%2C%20hope%20in%20darkness&width=600&height=400&seq=story005&orientation=landscape",
    content: "A year after the massacre, Jordan was scavenging in a long-abandoned radio shack. Out of habit, she flicked on a ham radio. Static. Then — a voice, faint and broken: \'...anyone... this is Aidan... holed up at the old Hotel... repeat, Aidan is...\' The shock was physical. He was alive. The guilt, the grief, the hardened shell she\'d built — it all shattered in an instant. She had a reason to fight again.",
  },
];

export default function StorySection() {
  const [activeChapter, setActiveChapter] = useState("ch1");
  const active = chapters.find((c) => c.id === activeChapter)!;

  return (
    <section id="story" className="w-full bg-[#0A0A0A] py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.3em] uppercase block mb-4">
            [ Chapters of Survival ]
          </span>
          <h2 className="font-['Special_Elite'] text-white text-4xl md:text-5xl">
            The Road to <span className="text-[#C8922A]">Redemption</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Chapter List */}
          <div className="flex flex-col gap-2">
            {chapters.map((ch) => (
              <button
                key={ch.id}
                onClick={() => setActiveChapter(ch.id)}
                className={`text-left px-5 py-4 border transition-all duration-300 cursor-pointer ${
                  activeChapter === ch.id
                    ? "border-[#8B1A1A] bg-[#8B1A1A]/10"
                    : "border-[#2C2C2C] hover:border-[#555]"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className="font-['Share_Tech_Mono'] text-xs tracking-widest uppercase"
                    style={{ color: activeChapter === ch.id ? ch.color : "#555" }}
                  >
                    {ch.tag}
                  </span>
                  <span className="font-['Share_Tech_Mono'] text-[#444] text-xs">
                    {ch.date}
                  </span>
                </div>
                <div
                  className={`font-['Special_Elite'] text-lg transition-colors ${
                    activeChapter === ch.id ? "text-white" : "text-[#666]"
                  }`}
                >
                  {ch.title}
                </div>
              </button>
            ))}
          </div>

          {/* Chapter Detail */}
          <div className="lg:col-span-2 border border-[#2C2C2C] overflow-hidden">
            <div className="relative h-64 overflow-hidden">
              <img
                key={active.id}
                src={active.image}
                alt={`${active.title} – Jordan Reyes story chapter`}
                className="w-full h-full object-cover object-top transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
              <div className="absolute top-5 left-5">
                <span
                  className="font-['Share_Tech_Mono'] text-xs tracking-[0.25em] uppercase px-3 py-1.5 border"
                  style={{ color: active.color, borderColor: `${active.color}60` }}
                >
                  {active.tag}
                </span>
              </div>
            </div>

            <div className="p-8">
              <div className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-widest uppercase mb-2">
                {active.date}
              </div>
              <h3 className="font-['Special_Elite'] text-white text-3xl mb-1">
                {active.title}
              </h3>
              <div className="font-['Rajdhani'] text-[#C8922A] text-sm mb-6 tracking-wide">
                {active.subtitle}
              </div>
              <p className="font-['Rajdhani'] text-[#AAAAAA] text-base leading-relaxed">
                {active.content}
              </p>

              <div className="mt-8 flex items-center gap-3">
                {chapters.map((ch, i) => (
                  <button
                    key={ch.id}
                    onClick={() => setActiveChapter(ch.id)}
                    className={`h-1 transition-all duration-300 cursor-pointer ${
                      activeChapter === ch.id ? "bg-[#C8922A] w-8" : "bg-[#333] w-4 hover:bg-[#555]"
                    }`}
                    aria-label={`Chapter ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
